import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { InsurancePlan } from '@/types/insurancePlan';
import { InsurancePlanType } from '@/types/insurancePlanType';
import { InsurancePolicy } from '@/types/insurancePolicy';
import { InsuranceProvider } from '@/types/insuranceProvider';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax, { Params } from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const insuranceRepository = {
  async getInsurancePolicy({
    host,
    insurancePolicyId
  }: {
    host: string;
    insurancePolicyId: string;
  }): Promise<InsurancePolicy> {
    const path = `/api/dugout/insurance_policies/${insurancePolicyId}/`;
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getInsurancePolicies({
    host,
    params
  }: {
    host: string;
    params: Record<string, Params>;
  }): Promise<InsurancePolicy[]> {
    const path = `/api/dugout/insurance_policies/`;
    const response = await ajax.get({
      url: `${host}${path}`,
      params
    });

    return response.data;
  },

  async createInsurancePolicy({
    host,
    data
  }: {
    host: string;
    data: Partial<InsurancePolicy>;
  }): Promise<InsurancePolicy> {
    const path = `/api/dugout/insurance_policies/`;
    const response = await ajax.post({
      url: `${host}${path}`,
      data
    });

    return response.data;
  },

  async updateInsurancePolicy({
    host,
    insurancePolicyId,
    data
  }: {
    host: string;
    insurancePolicyId: string;
    data: Partial<InsurancePolicy>;
  }): Promise<InsurancePolicy> {
    const path = `/api/dugout/insurance_policies/${insurancePolicyId}/`;
    const response = await ajax.patch({
      url: `${host}${path}`,
      data
    });

    return response.data;
  },

  async deleteInsurancePolicy({
    host,
    insurancePolicyId
  }: {
    host: string;
    insurancePolicyId: string;
  }): Promise<void> {
    const path = `/api/dugout/insurance_policies/${insurancePolicyId}/`;
    const response = await ajax.delete({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getInsurancePriorityTypes({
    host
  }: {
    host: string;
  }): Promise<RepositoryResponse<string[]>> {
    try {
      const path = `/api/insurance/insurance_options/priority_types/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get insurance priority types.');
    }
  },

  async getInsuranceProviders({
    host
  }: {
    host: string;
  }): Promise<RepositoryResponse<InsuranceProvider[]>> {
    try {
      const path = `/api/insurance/insurance_options/providers/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get insurance providers.');
    }
  },

  async getPlans({ host }: { host: string }): Promise<RepositoryResponse<InsurancePlan[]>> {
    try {
      const path = '/api/insurance/insurance_options/plans/';
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get insurance plans.');
    }
  },

  async getPlanTypes({ host }: { host: string }): Promise<RepositoryResponse<InsurancePlanType[]>> {
    try {
      const path = '/api/insurance/insurance_options/insurance_types/';
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get insurance types.');
    }
  },

  async getInsuranceRelationships({
    host
  }: {
    host: string;
  }): Promise<RepositoryResponse<string[]>> {
    try {
      const path = `/api/insurance/insurance_options/relationships/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get insurance relationships.');
    }
  }
};

const InsuranceRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getInsurancePolicy(insurancePolicyId: string): Promise<InsurancePolicy> {
      return insuranceRepository.getInsurancePolicy({ host, insurancePolicyId });
    },

    async getInsurancePolicies(params: Record<string, Params>): Promise<InsurancePolicy[]> {
      return insuranceRepository.getInsurancePolicies({ host, params });
    },

    async createInsurancePolicy(data: Partial<InsurancePolicy>): Promise<InsurancePolicy> {
      return insuranceRepository.createInsurancePolicy({ host, data });
    },

    async updateInsurancePolicy(
      insurancePolicyId: string,
      data: Partial<InsurancePolicy>
    ): Promise<InsurancePolicy> {
      return insuranceRepository.updateInsurancePolicy({ host, insurancePolicyId, data });
    },

    async deleteInsurancePolicy(insurancePolicyId: string): Promise<void> {
      return insuranceRepository.deleteInsurancePolicy({ host, insurancePolicyId });
    },

    async getInsurancePriorityTypes(): Promise<RepositoryResponse<string[]>> {
      return insuranceRepository.getInsurancePriorityTypes({ host });
    },

    async getInsuranceProviders(): Promise<RepositoryResponse<InsuranceProvider[]>> {
      return insuranceRepository.getInsuranceProviders({ host });
    },

    async getPlans(): Promise<RepositoryResponse<InsurancePlan[]>> {
      return insuranceRepository.getPlans({ host });
    },

    async getPlanTypes(): Promise<RepositoryResponse<InsurancePlanType[]>> {
      return insuranceRepository.getPlanTypes({ host });
    },

    async getInsuranceRelationships(): Promise<RepositoryResponse<string[]>> {
      return insuranceRepository.getInsuranceRelationships({ host });
    }
  };
};

export default InsuranceRepository;
